import { useEffect } from 'react';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';

export interface RollModeMenuState {
  x: number;
  y: number;
  label: string;
}

/**
 * Small context menu shared by every visible roll control.
 * Left-click keeps the currently selected mode; right-click lets the player
 * choose a one-off Straight / Advantage / Disadvantage roll without changing
 * that selected default.
 */
export function RollModeContextMenu({
  menu,
  onChoose,
  onClose,
}: {
  menu: RollModeMenuState | null;
  onChoose: (mode: RollMode) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!menu) return;
    const close = () => onClose();
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('click', close);
    window.addEventListener('blur', close);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('blur', close);
      window.removeEventListener('keydown', key);
    };
  }, [menu, onClose]);

  if (!menu) return null;
  return (
    <div
      className="dcc-roll-context-menu"
      role="menu"
      aria-label={`${menu.label} roll mode`}
      style={{ left: menu.x, top: menu.y }}
      onClick={(event) => event.stopPropagation()}
    >
      <strong>{menu.label}</strong>
      <button type="button" role="menuitem" onClick={() => onChoose('normal')}>Roll straight</button>
      <button type="button" role="menuitem" onClick={() => onChoose('advantage')}>Roll with Advantage</button>
      <button type="button" role="menuitem" onClick={() => onChoose('disadvantage')}>Roll with Disadvantage</button>
    </div>
  );
}
